import { useState } from "react";
import { Camera, Send, X, MessageSquare } from "lucide-react";
import {
  Button,
  Textarea,
  Image,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
  ScrollArea,
  Form,
  FormControl,
  FormField,
  FormItem
} from "@city-sage/aurum-avis-labs-components";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useRepository } from "@city-sage/aurum-avis-labs-components";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { QueryKeys } from "../../lib/QueryKeys";
import { PostPostableTypeEnum, Post } from "@city-sage/core-client";
  import { PostCard } from "./PostCard";
import { uploadFileCompressed } from "../../lib/imageUtils";

// Form schema
const createPostSchema = z.object({
  text: z.string().optional(),
  imageFile: z.instanceof(File).optional()
});

type CreatePostFormValues = z.infer<typeof createPostSchema>;

interface CreatePostSheetProps {
  isOpen: boolean;
  onClose: () => void;
  postableType: PostPostableTypeEnum;
  postableId: string;
  placeholder?: string;
}

export function CreatePostSheet({
  isOpen,
  onClose,
  postableType,
  postableId,
  placeholder
}: CreatePostSheetProps) {
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const { api } = useRepository();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const form = useForm<CreatePostFormValues>({
    resolver: zodResolver(createPostSchema),
    defaultValues: {
      text: "",
      imageFile: undefined
    }
  });

  const { handleSubmit, watch, setValue, reset, formState: { isSubmitting } } = form;
  const watchedImageFile = watch("imageFile");
  const watchedText = watch("text");

  // Fetch existing posts with regular query
  const {
    data: posts,
    isLoading: isLoadingPosts,
    error: postsError
  } = useQuery({
    queryKey: QueryKeys.post.byPostable(postableType.toUpperCase(), postableId),
    queryFn: () => api.findAllPost({
      filter: JSON.stringify({
        postableType: postableType.toUpperCase(),
        postableId
      }),
      limit: 1000,
    }),
    enabled: !!postableId && isOpen
  });

  const createPostMutation = useMutation({
    mutationFn: async (data: CreatePostFormValues) => {
      let uploadedFile = null;

      // Upload image if provided
      if (data.imageFile) {
        uploadedFile = await uploadFileCompressed(data.imageFile, api, {
          maxWidth: 1920,
          maxHeight: 1080,
          quality: 0.8
        });
      }

      // Create post
      return api.createPost({createPostRequest: {
        text: data.text,
        fileId: uploadedFile?.id,
        postableType: postableType,
        postableId: postableId
      }});
    },
    onSuccess: () => {
      // Invalidate the infinite query to refresh the posts
      queryClient.invalidateQueries({
        queryKey: QueryKeys.post.byPostable(postableType.toUpperCase(), postableId)
      });
      toast.success(t('posts.create.success'));
      reset();
      setImagePreview(null);
    },
    onError: (error) => {
      console.error('Failed to create post:', error);
      toast.error(t('posts.create.error'));
    }
  });

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setValue("imageFile", file);

      // Show preview immediately
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const onSubmit = (data: CreatePostFormValues) => {
    if (!data.text?.trim() && !data.imageFile) {
      toast.error(t('posts.create.validation.emptyPost'));
      return;
    }

    createPostMutation.mutate({
      text: data.text?.trim() || undefined,
      imageFile: data.imageFile
    });
  };

  const handleClose = () => {
    reset();
    setImagePreview(null);
    onClose();
  };

  const removeImage = () => {
    setValue("imageFile", undefined);
    setImagePreview(null);
  };

  return (
    <Drawer repositionInputs={false} open={isOpen} onOpenChange={handleClose}>
      <DrawerContent className="h-[90dvh]">
        {/* Fixed Header */}
        <DrawerHeader className="px-4 py-4 border-b border-border flex-shrink-0">
          <DrawerTitle>Posts</DrawerTitle>
        </DrawerHeader>

        {/* Scrollable Posts Area */}
        <ScrollArea className="flex-1 p-4">
          <div>
              {isLoadingPosts ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="animate-pulse">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="h-10 w-10 bg-muted rounded-full"></div>
                        <div className="space-y-1">
                          <div className="h-4 bg-muted rounded w-24"></div>
                          <div className="h-3 bg-muted rounded w-16"></div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="h-4 bg-muted rounded w-full"></div>
                        <div className="h-4 bg-muted rounded w-3/4"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : postsError ? (
                <div className="text-center text-destructive py-8">
                  <p>Failed to load posts. Please try again.</p>
                </div>
              ) : posts && posts.length > 0 ? (
                <div className="space-y-4">
                  {posts.map((post: Post) => (
                    <PostCard
                      key={post.id}
                      post={post}
                    />
                  ))}
                </div>
              ) : (
                <div className="py-12 text-center text-muted-foreground">
                  <MessageSquare className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                  <p className="text-sm">
                    No posts yet. Be the first to share your thoughts!
                  </p>
                </div>
              )}
            </div>
          </ScrollArea>

        {/* Fixed Input Section */}
        <DrawerFooter className="p-4">
          <Form {...form}>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
              {/* Image Preview */}
              {imagePreview && (
                <div className="flex items-start gap-2">
                  <div className="w-16 h-16 rounded-lg overflow-hidden border border-border">
                    <Image
                      src={imagePreview}
                      alt="Image preview"
                      className="w-full h-full"
                      imageClassName="object-cover !h-full"
                    />
                  </div>
                  <Button
                    type="button"
                    onClick={removeImage}
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              )}

              {/* Input Row */}
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <FormField
                    control={form.control}
                    name="text"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Textarea
                            {...field}
                            placeholder={placeholder || t(`posts.create.placeholder.${postableType}`)}
                            className="resize-none min-h-[80px] max-h-[120px]"
                            rows={3}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="cursor-pointer">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-10 w-10 p-0"
                      asChild
                    >
                      <span>
                        <Camera className="h-4 w-4" />
                      </span>
                    </Button>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </label>
                  <Button
                    type="submit"
                    disabled={createPostMutation.isPending || (!watchedText?.trim() && !watchedImageFile)}
                    size="sm"
                    className="h-10 w-10 p-0"
                  >
                    {createPostMutation.isPending ? (
                      <div className="h-4 w-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            </form>
          </Form>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
